import { useState, useEffect, useRef } from 'react'
import { currentLocale } from '../../i18n'
import {
  previewComprasImportacion, confirmComprasImportacion, getComprasImportaciones, descargarArchivoImportacion,
} from '../../api/client'
import consultaHana from '../../assets/sap/rempmo_exporte_compras_hana.sql?raw'

const num = (v, max = 2) => new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: max }).format(v)

function fmtDate(iso) {
  if (!iso) return '—'
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(currentLocale(), { day: 'numeric', month: 'short', year: 'numeric' })
}
function fmtDateTime(iso) {
  if (!iso) return '—'
  return new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString(currentLocale(), {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}
const MONEY_FIELDS = new Set(['total_clp', 'facturado_clp', 'factura_pagada_clp'])
const clp = (v) => (v == null ? '—' : `$${num(v, 0)}`)

// A SAP value, as SAP sent it: an empty one is a dash.
function valor(key, v) {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'Y' : 'N'
  if (/^fecha_/.test(key)) return fmtDate(v)
  if (MONEY_FIELDS.has(key)) return clp(v)
  if (typeof v === 'number') return num(v, 4)
  return String(v)
}


// ── Importer ───────────────────────────────────────────────────────────────
// The query that produces the Excel lives in assets/sap as a file, so it is edited as one.
// Until it holds a SELECT (the validated v3 is yet to be pasted there) the button says so.
const CONSULTA_LISTA = /\bselect\b/i.test(consultaHana)

function CopiarConsulta({ t }) {
  const [copiado, setCopiado] = useState(false)
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(consultaHana)
    } catch {
      const ta = document.createElement('textarea')       // where the clipboard API is not allowed
      ta.value = consultaHana
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    setCopiado(true)
    setTimeout(() => setCopiado(false), 2500)
  }
  return (
    <button type="button" onClick={copiar} disabled={!CONSULTA_LISTA}
      title={CONSULTA_LISTA ? t('importer.copyHint') : t('importer.copyMissing')}
      className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
      {copiado ? `✓ ${t('importer.copied')}` : `⧉ ${t('importer.copy')}`}
    </button>
  )
}

// The log of loads: only the last two stay, each with its file.
function UltimasCargas({ t, refresco }) {
  const [lista, setLista] = useState(null)
  const [err, setErr] = useState('')
  useEffect(() => {
    let vivo = true
    getComprasImportaciones().then(r => vivo && setLista(r)).catch(() => vivo && setLista([]))
    return () => { vivo = false }
  }, [refresco])
  const bajar = async (c) => {
    setErr('')
    try { await descargarArchivoImportacion(c.id, c.archivo) } catch (e) { setErr(e.response?.status === 404 ? t('importer.fileGone') : t('importer.fileFailed')) }
  }
  if (!lista) return null
  return (
    <div className="rounded-lg border border-gray-200 px-4 py-3 text-xs text-gray-600 space-y-2">
      <div className="font-medium text-gray-700">{t('importer.lastLoads')}</div>
      <p className="text-gray-400">{t('importer.lastLoadsHint')}</p>
      {err && <p className="text-red-600">{err}</p>}
      {lista.length === 0 ? <p>{t('importer.noLoads')}</p> : (
        <ul className="space-y-2">
          {lista.map(c => (
            <li key={c.id} className="flex flex-wrap items-start justify-between gap-2 border-t first:border-t-0 pt-2 first:pt-0">
              <div className="min-w-0">
                <div className="text-gray-800 font-medium truncate">{c.archivo || `#${c.id}`}</div>
                <div>{fmtDateTime(c.creada_en)} · {c.usuario || '—'} · {t('banner.dataAt', { date: fmtDate(c.datos_al) })}</div>
                <div className="text-gray-500">
                  {t('importer.orders')}: {num(c.pedidos, 0)} ({t('importer.new')} {num(c.nuevas, 0)}, {t('importer.updated')} {num(c.actualizadas, 0)})
                  {Object.entries(c.catalogos || {}).map(([k, v]) => ` · ${t(`importer.cat.${k}`)}: ${num(v.leidos, 0)} (+${num(v.nuevos, 0)}, ~${num(v.cambiados, 0)})`)}
                </div>
              </div>
              {c.con_archivo && (
                <button type="button" onClick={() => bajar(c)} className="px-3 py-1 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shrink-0">↓ {t('importer.downloadFile')}</button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// The query text, to get the Excel again in the same shape.
function DescargarConsulta({ t }) {
  const bajar = () => {
    const url = URL.createObjectURL(new Blob([consultaHana], { type: 'text/plain;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = 'rempmo_exporte_sap_hana.sql'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
  return (
    <button type="button" onClick={bajar} disabled={!CONSULTA_LISTA}
      className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
      ↓ {t('importer.downloadSql')}
    </button>
  )
}

function Cifra({ label, v, tone }) {
  return (
    <div className="rounded-lg border px-3 py-2 text-center">
      <div className={`text-xl font-semibold ${tone || 'text-gray-800'}`}>{num(v, 0)}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  )
}

function Importador({ onClose, onDone, t }) {
  const [file, setFile] = useState(null)
  const [fase, setFase] = useState('elegir')   // elegir | leyendo | previa | cargando | listo
  const [previa, setPrevia] = useState(null)
  const [err, setErr] = useState('')
  const [resultado, setResultado] = useState(null)
  const input = useRef(null)

  const elegir = async (f) => {
    if (!f) return
    setFile(f); setErr(''); setFase('leyendo')
    try {
      setPrevia(await previewComprasImportacion(f)); setFase('previa')
    } catch (e) {
      setErr(e.response?.data?.detail || e.message); setFase('elegir')
    }
  }
  const cargar = async () => {
    setFase('cargando'); setErr('')
    try {
      setResultado(await confirmComprasImportacion(file)); setFase('listo'); onDone()
    } catch (e) {
      setErr(e.response?.data?.detail || e.message); setFase('previa')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={fase === 'cargando' ? undefined : onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 space-y-4" role="dialog" aria-label={t('importer.title')}>
        <h2 className="text-lg font-semibold text-gray-900">{t('importer.title')}</h2>
        <p className="text-sm text-gray-500">{t('importer.intro')}</p>
        {err && <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">{err}</p>}

        {(fase === 'elegir' || fase === 'leyendo') && (
          <div className="space-y-3">
            <input ref={input} type="file" accept=".xlsx" className="hidden" onChange={e => elegir(e.target.files?.[0])} />
            <div className="flex flex-wrap items-center gap-2">
              <button disabled={fase === 'leyendo'} onClick={() => input.current?.click()}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                {fase === 'leyendo' ? t('importer.reading') : t('importer.choose')}
              </button>
              <CopiarConsulta t={t} />
              <DescargarConsulta t={t} />
            </div>
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-xs text-gray-600 space-y-1.5">
              <div className="font-medium text-gray-700">{t('importer.howTitle')}</div>
              <ol className="list-decimal pl-4 space-y-1">
                <li>{t('importer.how1')}</li>
                <li>{t('importer.how2')}</li>
                <li>{t('importer.how3')}</li>
              </ol>
              <p className="text-amber-700">{t('importer.howSolicitud')}</p>
              {!CONSULTA_LISTA && <p className="text-red-600">{t('importer.copyMissing')}</p>}
            </div>
            <UltimasCargas t={t} refresco={fase} />
          </div>
        )}

        {(fase === 'previa' || fase === 'cargando') && previa && (
          <>
            <h3 className="text-sm font-semibold text-gray-700">{t('importer.previewTitle')} <span className="font-normal text-gray-400">· {previa.archivo}</span></h3>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              <Cifra label={t('importer.lines')} v={previa.lineas} />
              <Cifra label={t('importer.orders')} v={previa.pedidos} />
              <Cifra label={t('importer.new')} v={previa.nuevas} tone="text-emerald-600" />
              <Cifra label={t('importer.updated')} v={previa.actualizadas} tone="text-amber-600" />
              <Cifra label={t('importer.same')} v={previa.sin_cambios} />
              <Cifra label={t('importer.review')} v={previa.a_revisar} tone={previa.a_revisar ? 'text-red-600' : undefined} />
            </div>
            {previa.catalogos && Object.keys(previa.catalogos).length > 0 && (
              <div className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-600">
                <div className="font-medium text-gray-700 mb-1">{t('importer.catalogs')}</div>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5">
                  {Object.entries(previa.catalogos).map(([k, v]) => (
                    <li key={k}>{t(`importer.cat.${k}`)}: <strong>{num(v.leidos, 0)}</strong> · {t('importer.new')} {num(v.nuevos, 0)} · {t('importer.updated')} {num(v.cambiados, 0)}</li>
                  ))}
                </ul>
              </div>
            )}
            {previa.ya_importado && (
              <p className="text-sm text-sky-700 bg-sky-50 rounded px-3 py-2">{t('importer.alreadyLoaded', { when: fmtDateTime(previa.ya_importado.creada_en) })}</p>
            )}
            {previa.faltantes?.length > 0 && (
              <p className="text-sm text-amber-700 bg-amber-50 rounded px-3 py-2">{t('importer.missingCols', { cols: previa.faltantes.join(', ') })}</p>
            )}
            {previa.cambios_por_campo && Object.keys(previa.cambios_por_campo).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">{t('importer.byField')}</h4>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(previa.cambios_por_campo).map(([campo, n]) => (
                    <span key={campo} className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-xs">
                      {t(`fields.${campo}`, campo)} <strong>{num(n, 0)}</strong>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {previa.cambios.length > 0 ? (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">{t('importer.changes')}</h4>
                <div className="border rounded-lg max-h-56 overflow-y-auto text-xs">
                  <table className="w-full">
                    <tbody>
                      {previa.cambios.slice(0, 80).map((c, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-2 py-1 text-gray-500 whitespace-nowrap">{c.pedido_num ?? c.pedido_id}·{c.linea}</td>
                          <td className="px-2 py-1 text-gray-700">{t(`fields.${c.campo}`, c.campo)}</td>
                          <td className="px-2 py-1 text-gray-400 line-through max-w-[10rem] truncate">{valor(c.campo, c.antes)}</td>
                          <td className="px-2 py-1 text-gray-800 max-w-[10rem] truncate">{valor(c.campo, c.despues)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(previa.cambios_truncados || previa.cambios.length > 80) && (
                  <p className="text-xs text-gray-400 mt-1">{t('importer.changesMore', { n: Math.min(previa.cambios.length, 80) })}</p>
                )}
              </div>
            ) : (
              previa.nuevas === 0 && <p className="text-sm text-gray-500">{t('importer.noChanges')}</p>
            )}
            {previa.casos_a_revisar.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">{t('importer.cases')} ({previa.a_revisar})</h4>
                <ul className="border rounded-lg max-h-40 overflow-y-auto text-xs divide-y">
                  {previa.casos_a_revisar.map((c, i) => <li key={i} className="px-2 py-1 text-gray-700">{c.detalle}</li>)}
                </ul>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} disabled={fase === 'cargando'} className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">{t('importer.cancel')}</button>
              <button onClick={cargar} disabled={fase === 'cargando'}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                {fase === 'cargando' ? t('importer.confirming') : t('importer.confirm')}
              </button>
            </div>
          </>
        )}

        {fase === 'listo' && resultado && (
          <>
            <p className="text-sm text-emerald-700 bg-emerald-50 rounded px-3 py-2">{t('importer.done', resultado)}</p>
            <div className="flex justify-end"><button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-800 text-white text-sm">{t('importer.close')}</button></div>
          </>
        )}
      </div>
    </div>
  )
}

export { Importador }

// The SAP load as a section of a page of its own (Operaciones > Carga masiva): what the file brings, the query that
// produces it (copy / .sql), the last two loads, and the button that opens the load window. `t` is the "compras"
// namespace translator.
export function PanelCargaSap({ t, onDone }) {
  const [abierto, setAbierto] = useState(false)
  const [refresco, setRefresco] = useState(0)
  return (
    <div className="max-w-3xl space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <h2 className="text-base font-semibold text-gray-900">{t('importer.title')}</h2>
        <p className="text-sm text-gray-500">{t('importer.intro')}</p>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setAbierto(true)} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">
            {t('importer.choose')}
          </button>
          <CopiarConsulta t={t} />
          <DescargarConsulta t={t} />
        </div>
        <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-xs text-gray-600 space-y-1.5">
          <div className="font-medium text-gray-700">{t('importer.howTitle')}</div>
          <ol className="list-decimal pl-4 space-y-1">
            <li>{t('importer.how1')}</li>
            <li>{t('importer.how2')}</li>
            <li>{t('importer.how3')}</li>
          </ol>
          {!CONSULTA_LISTA && <p className="text-red-600">{t('importer.copyMissing')}</p>}
        </div>
      </div>
      <UltimasCargas t={t} refresco={refresco} />
      {abierto && <Importador t={t} onClose={() => setAbierto(false)} onDone={() => { setRefresco(n => n + 1); onDone?.() }} />}
    </div>
  )
}
